import { Auth0Provider } from '@auth0/auth0-react'
import { createRootRoute, Navigate, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { ErrorRecoveryScreen } from '@/components/system-screens'
import { classifyError } from '@/features/system/error-recovery'
import { GLOBAL_RECOVERY_EVENT } from '@/features/system/global-recovery'
import type { GlobalRecoveryKind } from '@/features/system/global-recovery'
import { isMaintenancePeriod, MAINTENANCE_END_AT, MAINTENANCE_START_AT } from '@/features/system/maintenance-policy'
import { env } from '@/lib/env'
import { reportError } from '@/lib/monitoring'

const RootComponent = () => {
  const navigate = useNavigate()
  const [globalRecovery, setGlobalRecovery] = useState<GlobalRecoveryKind | null>(null)
  useEffect(() => {
    const handleRecovery = (event: Event) => {
      if (event.type === `${GLOBAL_RECOVERY_EVENT}:stale-client`) {
        setGlobalRecovery('stale-client')
      }
      if (event.type === `${GLOBAL_RECOVERY_EVENT}:unauthenticated`) {
        setGlobalRecovery('unauthenticated')
      }
    }
    const staleEvent = `${GLOBAL_RECOVERY_EVENT}:stale-client`
    const unauthenticatedEvent = `${GLOBAL_RECOVERY_EVENT}:unauthenticated`
    window.addEventListener(staleEvent, handleRecovery)
    window.addEventListener(unauthenticatedEvent, handleRecovery)
    return () => {
      window.removeEventListener(staleEvent, handleRecovery)
      window.removeEventListener(unauthenticatedEvent, handleRecovery)
    }
  }, [])
  const maintenance = isMaintenancePeriod({
    endAt: MAINTENANCE_END_AT,
    skipFlag: window.localStorage.getItem('skipMaintenance') ?? window.localStorage.getItem('skipMaintainance'),
    startAt: MAINTENANCE_START_AT,
  })
  if (maintenance && window.location.pathname !== '/maintenance') {
    return <Navigate to="/maintenance" replace />
  }
  if (!maintenance && window.location.pathname === '/maintenance') {
    return <Navigate to="/" replace />
  }
  return (
    <Auth0Provider
      domain={env.auth0Domain}
      clientId={env.auth0ClientId}
      cacheLocation="localstorage"
      authorizationParams={{ redirect_uri: window.location.origin }}
    >
      <div className="app-frame">
        <ErrorBoundary
          onError={(error) => {
            // ErrorBoundary は (error, info) を渡すため、reportError をそのまま渡すと
            // 第2引数の severity に componentStack が入ってしまう
            reportError(error)
          }}
          fallbackRender={({ error }) => {
            const normalizedError = error instanceof Error ? error : new Error('予期しないエラーが発生しました。')
            return (
              <ErrorRecoveryScreen
                kind={classifyError(normalizedError)}
                message={normalizedError.message}
                onHome={() => void navigate({ to: '/', replace: true })}
                onReload={() => {
                  window.location.reload()
                }}
                onSignOut={() => void navigate({ to: '/sign_out', replace: true })}
              />
            )
          }}
        >
          <Outlet />
        </ErrorBoundary>
        {globalRecovery !== null && (
          <div className="modal-backdrop z-[100]">
            <ErrorRecoveryScreen
              kind={globalRecovery}
              message={
                globalRecovery === 'unauthenticated'
                  ? 'セッションの有効期限が切れました。再ログインすると未保存の写真は失われます。未保存件数を確認してから進んでください。'
                  : undefined
              }
              onHome={() => void navigate({ to: '/', replace: true })}
              onReload={() => {
                window.location.reload()
              }}
              onSignOut={() => void navigate({ to: '/sign_out', replace: true })}
            />
          </div>
        )}
      </div>
    </Auth0Provider>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
})
